import React from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, Paper, Stack, TableContainer, Typography } from '@mui/material';
import { MODERN_COLORS, MODERN_RADIUS, MODERN_SHADOWS } from './tokens';

export const ModernTableContainer = ({
  title = 'Listado',
  count,
  countLabel = 'registros encontrados',
  selectedCount = 0,
  metrics = [],
  toolbarActions,
  children,
  sx = {},
}) => {
  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        display: { xs: 'none', md: 'block' },
        bgcolor: MODERN_COLORS.bgCard,
        borderRadius: MODERN_RADIUS.table,
        border: `1.5px solid ${MODERN_COLORS.borderCyan}`,
        boxShadow: MODERN_SHADOWS.table,
        width: '100%',
        overflowX: 'auto',
        overflow: 'hidden',
        transition: 'box-shadow 0.2s ease',
        '&:hover': {
          boxShadow: MODERN_SHADOWS.tableHover,
        },
        ...sx,
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          bgcolor: MODERN_COLORS.bgLight,
          borderBottom: `1.5px solid ${MODERN_COLORS.borderSoft}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              bgcolor: MODERN_COLORS.primary,
              color: '#ffffff',
              px: 1.5,
              py: 0.4,
              borderRadius: MODERN_RADIUS.pill,
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            {title}
          </Box>
          {count !== undefined && (
            <Typography variant="caption" sx={{ color: MODERN_COLORS.primaryDark, fontWeight: 600 }}>
              {count} {countLabel}
            </Typography>
          )}
        </Stack>

        <Stack direction="row" spacing={1.5} alignItems="center">
          {selectedCount > 0 && (
            <Chip
              size="small"
              label={`${selectedCount} seleccionados`}
              sx={{
                bgcolor: MODERN_COLORS.primaryIce2,
                color: MODERN_COLORS.primaryDark,
                border: `1px solid ${MODERN_COLORS.primaryLight}`,
                fontWeight: 700,
                borderRadius: MODERN_RADIUS.pill,
              }}
            />
          )}

          {metrics.map((metric, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.8,
                px: 1.5,
                py: 0.4,
                borderRadius: MODERN_RADIUS.pill,
                bgcolor: metric.bgColor || MODERN_COLORS.bgLight,
                border: `1.2px solid ${metric.borderColor || MODERN_COLORS.borderSoft}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: metric.color || MODERN_COLORS.primaryDark,
                  textTransform: 'uppercase',
                  fontSize: '0.675rem',
                }}
              >
                {metric.label}:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 800,
                  color: metric.textColor || metric.color || MODERN_COLORS.primaryDark,
                  fontSize: '0.85rem',
                }}
              >
                {metric.value}
              </Typography>
            </Box>
          ))}

          {toolbarActions}
        </Stack>
      </Box>

      {children}
    </TableContainer>
  );
};

ModernTableContainer.propTypes = {
  title: PropTypes.string,
  count: PropTypes.number,
  countLabel: PropTypes.string,
  selectedCount: PropTypes.number,
  metrics: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      color: PropTypes.string,
      bgColor: PropTypes.string,
      borderColor: PropTypes.string,
      textColor: PropTypes.string,
    })
  ),
  toolbarActions: PropTypes.node,
  children: PropTypes.node.isRequired,
  sx: PropTypes.object,
};

export default ModernTableContainer;
